import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Users as UsersIcon,
  Zap,
  TrendingUp,
  Download,
  RefreshCcw,
  Target,
  Activity,
  DollarSign,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const RANGE_OPTIONS = [
  { key: 1, label: "Today" },
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
  { key: 3650, label: "All time" },
];

function formatPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${n}%`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function KPI({ label, value, sub, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`border ${accent ? "border-coral bg-coral-subtle" : "border-neutral-200 bg-white"} p-5`}
    >
      <p className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{value}</p>
      {sub && <p className="mt-1 text-xs text-neutral-500">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="mb-4 mt-10 flex flex-wrap items-end justify-between gap-3 border-b border-neutral-200 pb-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">— {title}</p>
        {subtitle && <p className="mt-1 text-xs text-neutral-500">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function FunnelBar({ stage, count, pctTotal, pctPrev, maxCount }) {
  const width = maxCount ? Math.max(3, Math.round((count * 100) / maxCount)) : 0;
  return (
    <div className="border-b border-neutral-100 py-3 last:border-b-0" data-testid={`funnel-${stage}`}>
      <div className="mb-2 flex items-center justify-between gap-3 text-xs">
        <span className="text-neutral-800">{stage}</span>
        <span className="font-mono text-neutral-500">
          {count} · <span className="text-neutral-400">{formatPct(pctTotal)}</span>{" "}
          <span className="text-coral">{pctPrev < 100 ? `↓ ${formatPct(pctPrev)}` : ""}</span>
        </span>
      </div>
      <div className="h-2 w-full bg-neutral-100">
        <div className="h-full bg-coral" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function TinyBarChart({ points }) {
  const max = Math.max(1, ...points.map((p) => p.count));
  return (
    <div className="flex h-32 items-end gap-1" data-testid="analytics-daily-chart">
      {points.length === 0 ? (
        <p className="w-full text-center text-sm text-neutral-500">No data in this range.</p>
      ) : (
        points.map((p) => (
          <div key={p.day} className="flex flex-1 flex-col items-center gap-1" title={`${p.day} · ${p.count}`}>
            <div
              className="w-full bg-coral transition-colors hover:bg-coral-hover"
              style={{ height: `${Math.max(2, (p.count / max) * 100)}%` }}
            />
            <span className="hidden text-[9px] text-neutral-400 sm:block">{p.day.slice(5)}</span>
          </div>
        ))
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [usersRows, setUsersRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async (d = range) => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([
        api.get(`/admin/analytics?days=${d}`),
        api.get("/admin/analytics/users?limit=500&sort=activity"),
      ]);
      setData(a.data);
      setUsersRows(u.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load(range);
  }, [isAdmin, range]);

  const funnelMax = useMemo(
    () => Math.max(1, ...(data?.funnel || []).map((s) => s.count)),
    [data]
  );

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/analytics/users.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `postshifted_users_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  if (authLoading || (isAdmin && loading && !data)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
        <div
          data-testid="analytics-loading"
          className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black"
        />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center lg:px-10">
          <ShieldCheck className="mx-auto mb-4 h-8 w-8 text-neutral-400" strokeWidth={1.5} />
          <h1 data-testid="analytics-forbidden" className="font-serif text-4xl font-bold">
            Admin access only
          </h1>
        </main>
        <Footer />
      </div>
    );
  }

  const s = data?.summary || {};
  const users = data?.users || {};
  const gens = data?.generations || {};
  const formats = data?.formats?.usage || [];
  const rep = data?.repurpose || {};
  const ret = data?.retention || {};
  const freePlan = data?.free_plan || {};
  const conv = data?.conversion || {};

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-500">
              <Activity className="h-3.5 w-3.5" strokeWidth={1.5} />
              — Analytics
            </p>
            <h1
              data-testid="analytics-title"
              className="font-serif text-4xl font-bold tracking-tight sm:text-5xl"
            >
              PostShifted health
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-neutral-600">
              How free users become paying users, and where the drop-off happens.{" "}
              <Link to="/admin" className="underline underline-offset-4">Back to Admin</Link>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center border border-neutral-300 bg-white p-1">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRange(r.key)}
                  data-testid={`analytics-range-${r.key}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                    range === r.key ? "bg-black text-white" : "text-neutral-600 hover:text-black"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => load(range)}
              data-testid="analytics-refresh"
              className="h-11 rounded-[3px] border-neutral-300 text-xs"
            >
              <RefreshCcw className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Refresh
            </Button>
            <Button
              onClick={downloadCsv}
              disabled={downloading}
              data-testid="analytics-csv-export"
              className="h-11 rounded-[3px] bg-coral px-5 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50"
            >
              <Download className="mr-2 h-4 w-4" strokeWidth={1.5} />
              {downloading ? "…" : "CSV export"}
            </Button>
          </div>
        </div>

        {/* Top opportunity banner */}
        {data?.top_opportunity && (
          <div
            data-testid="analytics-top-opportunity"
            className="mb-8 flex flex-wrap items-center gap-3 border-2 border-coral bg-coral-subtle p-5"
          >
            <Target className="h-6 w-6 flex-shrink-0 text-coral" strokeWidth={1.5} />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-coral">Top conversion opportunity</p>
              <p className="mt-1 font-serif text-xl text-neutral-900">
                <span className="font-bold">{data.top_opportunity.count}</span> free users{" "}
                {data.top_opportunity.label}.
              </p>
            </div>
          </div>
        )}

        {/* KPI ROW */}
        <SectionHeader title="Health summary" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KPI testid="kpi-total-users" label="Total users" value={s.total_users ?? "—"} />
          <KPI testid="kpi-free" label="Free" value={s.free_users ?? "—"} />
          <KPI testid="kpi-pro" label="Pro" value={s.pro_users ?? "—"} accent />
          <KPI testid="kpi-active" label="Active 7d" value={s.active_users_7d ?? "—"} sub="Generated in last 7 days" />
          <KPI testid="kpi-gens" label="Generations" value={s.total_generations ?? "—"} />
          <KPI testid="kpi-at-limit" label="At free limit" value={s.users_at_free_limit_this_month ?? "—"} sub="This calendar month" />
          <KPI testid="kpi-pricing-views" label="Pricing views" value={s.pricing_views_total ?? "—"} />
          <KPI testid="kpi-checkouts" label="Checkout starts" value={s.checkout_starts_total ?? "—"} />
          <KPI testid="kpi-conv" label="Free → Pro" value={formatPct(s.free_to_pro_rate)} accent />
          <KPI testid="kpi-high-intent" label="High-intent free" value={freePlan.high_intent_count ?? "—"} sub="Signal-rich unconverted" />
        </div>

        {/* Activation rate row — new to focus on the top-of-funnel */}
        <SectionHeader
          title="Activation focus"
          subtitle="Every → next-step conversion rate. This is what to move first."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KPI
            testid="kpi-activation"
            label="Activation rate"
            value={formatPct(s.activation_rate)}
            sub={`${users.generated_1_plus ?? 0} of ${users.total ?? 0} users generated at least once`}
            accent
          />
          <KPI
            testid="kpi-second-gen"
            label="Second-gen rate"
            value={formatPct(s.second_gen_rate)}
            sub={`${users.generated_2_plus ?? 0} of ${users.generated_1_plus ?? 0} activated users came back`}
          />
          <KPI
            testid="kpi-third-gen"
            label="Third-gen rate"
            value={formatPct(s.third_gen_rate)}
            sub={`${users.generated_3_plus ?? 0} of ${users.generated_1_plus ?? 0} activated users hit 3+ gens`}
          />
        </div>

        {data?.internal_excluded && (
          <p className="mt-3 text-xs text-neutral-500" data-testid="analytics-internal-note">
            {data.internal_count} internal / test account{data.internal_count === 1 ? "" : "s"} excluded from these totals (set via <code className="font-mono">INTERNAL_EMAILS</code> env var).
          </p>
        )}

        {/* FUNNEL */}
        <SectionHeader
          title="Activation → conversion funnel"
          subtitle="Every stage shows: count · % of all users · % from prior stage (arrows = drop-off)"
        />
        <div className="border border-neutral-200 bg-white p-5">
          {(data?.funnel || []).map((s) => (
            <FunnelBar key={s.stage} {...s} maxCount={funnelMax} pctTotal={s.pct_total} pctPrev={s.pct_prev} />
          ))}
        </div>

        {/* GENERATIONS OVER TIME */}
        <SectionHeader title="Generations over time" subtitle={`Last ${range} days`} />
        <div className="border border-neutral-200 bg-white p-5">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Total" value={gens.total ?? "—"} testid="gen-total" />
            <KPI label="Last 7d" value={gens.last_7d ?? "—"} testid="gen-7d" />
            <KPI label="Last 30d" value={gens.last_30d ?? "—"} testid="gen-30d" />
            <KPI label="Avg / active user" value={gens.avg_per_active_user ?? "—"} testid="gen-avg" />
          </div>
          <TinyBarChart points={gens.daily || []} />
        </div>

        {/* USER OVERVIEW */}
        <SectionHeader title="User overview" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KPI label="New 7d" value={users.new_7d ?? "—"} testid="users-new-7d" />
          <KPI label="New 30d" value={users.new_30d ?? "—"} testid="users-new-30d" />
          <KPI label="Never generated" value={users.never_generated ?? "—"} testid="users-never" sub={formatPct(gens.distribution?.never_pct)} />
          <KPI label="Gen ≥ 1" value={users.generated_1_plus ?? "—"} testid="users-1plus" />
          <KPI label="Gen ≥ 2" value={users.generated_2_plus ?? "—"} testid="users-2plus" />
          <KPI label="Gen ≥ 5" value={users.generated_5_plus ?? "—"} testid="users-5plus" />
          <KPI label="Returned" value={users.returned_after_first_session ?? "—"} testid="users-returned" sub="Any 2nd session ≤ 30d" />
        </div>

        {/* CONVERSION */}
        <SectionHeader title="Conversion by cohort" subtitle="Free → Pro rate segmented by intent signal" />
        <div className="overflow-hidden border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="conversion-table">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Cohort</th>
                <th className="px-4 py-3">Cohort size</th>
                <th className="px-4 py-3">Converted</th>
                <th className="px-4 py-3">Rate</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["All users", conv.overall],
                ["Reached free limit", conv.reached_limit],
                ["Generated 2+ times", conv.generated_2_plus],
                ["Generated 3+ times", conv.generated_3_plus],
                ["Generated 5+ times", conv.generated_5_plus],
                ["Viewed pricing", conv.viewed_pricing],
                ["Started checkout", conv.started_checkout],
              ].map(([label, row]) => (
                <tr key={label} className="border-b border-neutral-100 last:border-b-0">
                  <td className="px-4 py-3">{label}</td>
                  <td className="px-4 py-3 font-mono text-xs">{row?.total ?? 0}</td>
                  <td className="px-4 py-3 font-mono text-xs">{row?.converted ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block border border-coral bg-coral-subtle px-2 py-0.5 text-[10px] uppercase tracking-widest text-coral">
                      {formatPct(row?.rate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* FORMATS + REPURPOSE + RETENTION */}
        <SectionHeader title="Format usage · Repurpose · Retention" />
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="border border-neutral-200 bg-white p-5">
            <p className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Formats</p>
            {formats.length === 0 ? (
              <p className="text-sm text-neutral-500">No generation data yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {formats.map((f) => (
                  <li key={f.format} className="flex items-center justify-between">
                    <span>{f.format}</span>
                    <span className="font-mono text-xs text-neutral-500">
                      {f.count} · {formatPct(f.pct)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {data?.formats?.most_popular && (
              <p className="mt-4 text-xs text-neutral-500">
                Most popular: <span className="font-medium text-coral">{data.formats.most_popular}</span>
              </p>
            )}
          </div>
          <div className="border border-neutral-200 bg-white p-5" data-testid="repurpose-card">
            <p className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Repurpose</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total actions</span><span className="font-mono">{rep.total_actions ?? 0}</span></div>
              <div className="flex justify-between"><span>Users who used it</span><span className="font-mono">{rep.users_used ?? 0}</span></div>
              <div className="flex justify-between"><span>% of all users</span><span className="font-mono">{formatPct(rep.pct_users)}</span></div>
              <div className="flex justify-between"><span>Avg per repurposing user</span><span className="font-mono">{rep.avg_per_user ?? 0}</span></div>
              <div className="flex justify-between"><span>Last 7d</span><span className="font-mono">{rep.last_7d ?? 0}</span></div>
              <div className="flex justify-between"><span>Last 30d</span><span className="font-mono">{rep.last_30d ?? 0}</span></div>
              <div className="flex justify-between"><span>Free vs Pro actions</span><span className="font-mono">{rep.free_actions ?? 0} / {rep.pro_actions ?? 0}</span></div>
            </div>
          </div>
          <div className="border border-neutral-200 bg-white p-5" data-testid="retention-card">
            <p className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Retention</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>DAU</span><span className="font-mono">{ret.dau ?? 0}</span></div>
              <div className="flex justify-between"><span>WAU</span><span className="font-mono">{ret.wau ?? 0}</span></div>
              <div className="flex justify-between"><span>MAU</span><span className="font-mono">{ret.mau ?? 0}</span></div>
              <div className="flex justify-between"><span>Returned within 7d</span><span className="font-mono">{ret.returned_within_7d ?? 0} · {formatPct(ret.returned_pct_7d)}</span></div>
              <div className="flex justify-between"><span>Returned within 14d</span><span className="font-mono">{ret.returned_within_14d ?? 0} · {formatPct(ret.returned_pct_14d)}</span></div>
              <div className="flex justify-between"><span>Returned within 30d</span><span className="font-mono">{ret.returned_within_30d ?? 0} · {formatPct(ret.returned_pct_30d)}</span></div>
            </div>
            <p className="mt-3 text-[11px] text-neutral-400">Active = created a generation (meaningful product action).</p>
          </div>
        </div>

        {/* FREE PLAN DEEP DIVE */}
        <SectionHeader title="Free plan deep dive" subtitle="Where the funnel currently loses users" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <KPI label="Free total" value={freePlan.total ?? "—"} testid="free-total" />
          <KPI label="0 generations" value={freePlan.never_generated ?? "—"} testid="free-never" />
          <KPI label="1 generation" value={freePlan.generated_1 ?? "—"} testid="free-1" />
          <KPI label="2–4 generations" value={freePlan.generated_2_to_4 ?? "—"} testid="free-2to4" />
          <KPI label="Reached limit" value={freePlan.reached_limit ?? "—"} testid="free-limit" accent />
          <KPI label="Viewed pricing" value={freePlan.viewed_pricing ?? "—"} testid="free-pricing" />
          <KPI label="Started checkout" value={freePlan.started_checkout ?? "—"} testid="free-checkout" />
          <KPI label="Converted to Pro" value={freePlan.converted_to_pro ?? "—"} testid="free-converted" accent />
        </div>

        {/* USER TABLE */}
        <SectionHeader
          title="Users · sorted by most active"
          subtitle={`Showing ${Math.min(usersRows.length, 100)} of ${usersRows.length}. CSV export includes the full set.`}
        />
        <div className="overflow-x-auto border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="analytics-users-table">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3">Signup</th>
                <th className="px-3 py-3">Days</th>
                <th className="px-3 py-3">Gens</th>
                <th className="px-3 py-3">This mo</th>
                <th className="px-3 py-3">Repurp</th>
                <th className="px-3 py-3">Last activity</th>
                <th className="px-3 py-3">Limit</th>
                <th className="px-3 py-3">Pricing</th>
                <th className="px-3 py-3">Checkout</th>
                <th className="px-3 py-3">Pro</th>
              </tr>
            </thead>
            <tbody>
              {usersRows.slice(0, 100).map((r) => (
                <tr key={r.user_id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50" data-testid={`analytics-user-${r.user_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.email}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      r.plan === "pro" ? "border-coral bg-coral-subtle text-coral" : "border-neutral-300 text-neutral-600"
                    }`}>{r.plan}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.days_since_signup ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.generations}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.generations_this_month}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.repurposes}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.last_activity)}</td>
                  <td className="px-3 py-2">{r.reached_free_limit ? "✓" : ""}</td>
                  <td className="px-3 py-2">{r.viewed_pricing ? "✓" : ""}</td>
                  <td className="px-3 py-2">{r.started_checkout ? "✓" : ""}</td>
                  <td className="px-3 py-2">{r.converted_to_pro ? "✓" : ""}</td>
                </tr>
              ))}
              {usersRows.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-sm text-neutral-500">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Data quality note */}
        <p className="mt-8 text-xs text-neutral-500" data-testid="analytics-data-quality">
          Data quality: country / geo tracking is not collected (privacy). Repurpose actions and
          pricing/checkout events are tracked from the moment this dashboard was deployed — earlier
          activity is not backfilled.
        </p>
      </main>
      <Footer />
    </div>
  );
}
